import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { BehaviorSubject } from "rxjs";
import { loaderSubject } from "helpers/loader.helper";
import {
  getCookieItem,
  getFromLocalStorage,
  removeFromLocalStorage,
} from "core/utils/utils.helper";
import { GIGYA_LOGIN } from "constants/storage";
import { COUNTRY_SELECTION_STORAGE_NAME } from "constants/country";

// Extend Axios request config to include custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number; // Tracks how many times the request has been retried
}

// Type for queued promise callbacks in case of concurrent 401 errors
interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}

// Handling HTTP error globally
export const errorSubject = new BehaviorSubject<AxiosError | null>(null);

const requests: CustomAxiosRequestConfig[] = []; // Keeps track of active requests
let isRefreshing: boolean = false; // Prevents multiple token refreshes at the same time
let failedQueue: QueueItem[] = []; // Stores requests that fail due to a 401 error

const MAX_RETRY_ATTEMPTS: number = 1; // Maximum retry attempts for each request

// Remove a request from the queue
const removeRequest = (req: CustomAxiosRequestConfig): void => {
  const i = requests.indexOf(req);
  if (i >= 0) {
    requests.splice(i, 1);
  }
  loaderSubject.next(requests.length > 0); // Update loader state based on active requests
};

// Process the failed requests queue
const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
): void => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token); // Resolve with new token if available
    } else if (error) {
      prom.reject(error); // Reject if there was an error
    }
  });
  failedQueue = []; // Clear the queue after processing
};

// Get the UID from localStorage (this is the token to be sent in headers)
const token: string | null = getFromLocalStorage(GIGYA_LOGIN.KEY);
// Request interceptor
axios.interceptors.request.use(
  (config: CustomAxiosRequestConfig): CustomAxiosRequestConfig => {
    const cookieItem = getCookieItem(COUNTRY_SELECTION_STORAGE_NAME); // Read the current country from the cookie each time a request is made
    // Add the UID (token) and country to request headers
    if (config.headers) {
      config.headers.uid = token;
      config.timeout = 60000; // Set a timeout of 60 seconds for each request
      config.headers.country = cookieItem; // Always use the latest value from the cookie
    }

    config._retryCount = config._retryCount ?? 0; // Initialize retry count if not set
    requests.push(config); // Add the request to the active requests queue
    loaderSubject.next(true); // Indicate that a request is in progress
    return config; // Proceed with the request
  },
  (error: AxiosError): Promise<never> => {
    return Promise.reject(error); // Reject the promise immediately if request fails
  },
);

// Response interceptor
axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Successfully processed request, so remove it from the queue
    removeRequest(response.config as CustomAxiosRequestConfig);
    return response; // Return the response to the caller
  },
  async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    removeRequest(originalRequest); // Remove failed request from queue

    // Determine how many times the request has been retried
    const retryCount = originalRequest._retryCount ?? 0;

    // If the error is a 401 and retry count is less than the max, retry the request
    if (error.response?.status === 401 && retryCount < MAX_RETRY_ATTEMPTS) {
      if (isRefreshing) {
        // If another request is already refreshing the token, wait for it to finish
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject }); // Queue the failed request to retry later
        })
          .then((token: string) => {
            return axios(originalRequest); // Retry the request after obtaining a new token
          })
          .catch((err: AxiosError) => {
            return Promise.reject(err); // Reject if something goes wrong
          });
      }

      // Mark that the token is being refreshed
      originalRequest._retryCount = retryCount + 1;
      isRefreshing = true;

      try {
        // Process the failed requests before retrying
        processQueue(null, token);
        isRefreshing = false; // Reset flag after processing
        return axios(originalRequest); // Retry the request once more
      } catch (refreshError) {
        // Handle errors during token refresh
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;

        // If retry attempts exceed max retries, clear localStorage and log out the user to homepage
        if (originalRequest._retryCount >= MAX_RETRY_ATTEMPTS) {
          removeFromLocalStorage(); // Clear the token from localStorage
          window.location.href = "/"; // Redirect to the home page (or login)
          return Promise.reject(refreshError); // Reject with the refresh error
        }

        return Promise.reject(refreshError); // Reject the promise with the error
      }
    }

    // If retry attempts exceed max retries, clear localStorage and log out the user to homepage
    if (error.response?.status === 401 && retryCount >= MAX_RETRY_ATTEMPTS) {
      removeFromLocalStorage(); // Clear the token from localStorage
      window.location.href = "/"; // Redirect to the home page (or login)
      return Promise.reject(error); // Reject the promise with the error
    }

    // Global error handling
    errorSubject.next(error);
    return Promise.reject(error); // Reject the promise with the error
  },
);
// 👇 Export this configured instance
export const axiosInstance = axios;
